import { Divider, Stack } from '@mui/material';
import { compact, find, isEmpty, map, sortBy } from 'lodash';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CopyTextId, DescriptionRow } from 'components';
import { RequisitesCascadeMode } from 'enums';
import { useUserContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Cascade, CascadePayoutOrderAssignee, OrderAssigneeType } from 'types';

type Props = {
  cascade: Cascade | null;
};

export const CascadeDetails: React.FC<Props> = ({ cascade }) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.cascades.cascade_details',
  });
  const { t: tList } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.cascades.cascades_list.table',
  });

  const { p2pProviders } = useUserContext();

  const getCascadeModeLabel = useCallback(
    (mode: RequisitesCascadeMode) => {
      switch (mode) {
        case RequisitesCascadeMode.TradersFirst:
          return tList('modes.traders_first');
        case RequisitesCascadeMode.ProvidersFirst:
          return tList('modes.providers_first');
        case RequisitesCascadeMode.TradersOnly:
          return tList('modes.traders_only');
        case RequisitesCascadeMode.ProvidersOnly:
          return tList('modes.providers_only');
        case RequisitesCascadeMode.Parallel:
          return tList('modes.parallel');
        case RequisitesCascadeMode.ParallelPriority:
          return tList('modes.parallel_priority');
        case RequisitesCascadeMode.SequentialAssignees:
          return tList('modes.sequential_assignees');
        case RequisitesCascadeMode.SequentialProviders:
          return tList('modes.sequential_providers');
        default:
          return mode;
      }
    },
    [tList],
  );

  const getCascadeModeLabelPayout = useCallback(
    (mode: RequisitesCascadeMode) => {
      switch (mode) {
        case RequisitesCascadeMode.TradersFirst:
          return tList('modes.default');
        default:
          return getCascadeModeLabel(mode);
      }
    },
    [tList, getCascadeModeLabel],
  );

  const cascadeP2PProviders = useMemo(
    () =>
      compact(
        map(sortBy(cascade?.p2pProvidersConfigs || [], 'priority'), (config) =>
          find(p2pProviders, ({ id }) => id === config.p2pProviderId),
        ),
      ),
    [cascade?.p2pProvidersConfigs, p2pProviders],
  );

  const cascadePayoutP2PProviders = useMemo(
    () =>
      compact(
        map(
          sortBy(cascade?.payoutP2PProvidersConfigs || [], 'priority'),
          (config) =>
            find(p2pProviders, ({ id }) => id === config.p2pProviderId),
        ),
      ),
    [cascade?.payoutP2PProvidersConfigs, p2pProviders],
  );

  if (!cascade) {
    return null;
  }

  return (
    <Stack direction="column" spacing={6}>
      <Stack direction="column" spacing={3}>
        <DescriptionRow title={t('rows.id')} value={cascade.id} canCopy />
        <DescriptionRow title={t('rows.name')} value={cascade.name} />
        <DescriptionRow
          title={t('rows.payin_mode')}
          value={getCascadeModeLabel(cascade.payinCascadeMode)}
        />
        {cascade.payinCascadeMode ===
          RequisitesCascadeMode.ParallelPriority && (
          <DescriptionRow
            title={t('rows.payin_parallel_priority_timeout')}
            value={cascade.payinParallelPriorityTimeout}
          />
        )}
        {!isEmpty(cascadeP2PProviders) && (
          <Stack direction="column" spacing={3}>
            <div>{t('titles.p2p_providers')}</div>

            {map(cascadeP2PProviders, (provider) => (
              <Stack direction="column" spacing={0} key={provider.id}>
                <CopyTextId id={provider.id} />
                <div>{provider.name}</div>
              </Stack>
            ))}
          </Stack>
        )}
        <Divider />
        <DescriptionRow
          title={t('rows.payout_mode')}
          value={getCascadeModeLabelPayout(cascade.payoutCascadeMode)}
        />
        {!isEmpty(cascadePayoutP2PProviders) &&
          [
            RequisitesCascadeMode.TradersFirst,
            RequisitesCascadeMode.ProvidersFirst,
            RequisitesCascadeMode.ProvidersOnly,
            RequisitesCascadeMode.Parallel,
          ].includes(cascade.payoutCascadeMode) && (
            <Stack direction="column" spacing={3}>
              <div>{t('titles.p2p_providers')}</div>

              {map(cascadePayoutP2PProviders, (provider) => (
                <Stack direction="column" spacing={0} key={provider.id}>
                  <CopyTextId id={provider.id} />
                  <div>{provider.name}</div>
                </Stack>
              ))}
            </Stack>
          )}
        {!isEmpty(cascade?.payoutOrderAssignees) &&
          cascade.payoutCascadeMode ===
            RequisitesCascadeMode.SequentialAssignees && (
            <Stack direction="column" spacing={3}>
              <div>{t('titles.assignees')}</div>

              {map(
                sortBy(cascade?.payoutOrderAssignees || [], 'priority'),
                (assignee: CascadePayoutOrderAssignee) => (
                  <Stack direction="column" spacing={0} key={assignee.id}>
                    {assignee.assigneeType === OrderAssigneeType.Trader && (
                      <Fragment>
                        <div>{t('assignee_types.trader')}</div>
                        <div>
                          {assignee.trader?.user?.name || assignee.traderId}
                        </div>
                        <CopyTextId id={assignee.traderId} />
                      </Fragment>
                    )}
                    {assignee.assigneeType ===
                      OrderAssigneeType.P2PProvider && (
                      <Fragment>
                        <div>{t('assignee_types.p2p_provider')}</div>
                        <div>
                          {assignee.p2pProvider?.name || assignee.p2pProviderId}
                        </div>
                        <CopyTextId id={assignee.p2pProviderId} />
                      </Fragment>
                    )}
                    {assignee.assigneeType === OrderAssigneeType.AnyTrader && (
                      <div>{t('assignee_types.any_trader')}</div>
                    )}
                    <div>{t('rows.timeout', { value: assignee.timeout })}</div>
                  </Stack>
                ),
              )}
            </Stack>
          )}
      </Stack>
    </Stack>
  );
};
